import { Injectable } from '@angular/core';
import { EMPTY, map, Observable, tap } from 'rxjs';
import { mapToVoid } from '@examdojo/rxjs';
import { UserHttpModel, UserRole } from '@examdojo/models/user';
import { UserReviewModeBindingsPermissions } from '@examdojo/review';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { AdminUserViewHttpService } from './admin-user-view-http.service';
import { AdminUserStore } from './admin-user.store';
import { AdminUserHttpModel, AdminUserStoreModel } from './admin-user.model';
import { AdminUserQuery } from './admin-user.query';
import { UserRoleBindingsHttpService } from './user-role-bindings-http.service';

@Injectable({ providedIn: 'root' })
export class AdminUserService {
  constructor(
    private readonly adminUserHttpService: AdminUserViewHttpService,
    private readonly adminUserStore: AdminUserStore,
    private readonly adminUserQuery: AdminUserQuery,
    private readonly userRoleBindingsHttpService: UserRoleBindingsHttpService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  fetchAll(): Observable<void> {
    return this.adminUserHttpService.fetchListView().pipe(
      map((users) => this.mapToStoreModels(users)),
      tap((userStoreModels) => {
        this.adminUserStore.set(userStoreModels);
      }),
      mapToVoid(),
    );
  }

  updateStoreUserReviewPermissions(
    assignedSyllabuses: UserReviewModeBindingsPermissions[],
    userId: UserHttpModel['id'] | null = this.adminUserQuery.getActiveId(),
  ) {
    assertNonNullable(userId, 'userId');

    this.adminUserStore.update(userId, (user) => ({
      ...user,
      assigned_syllabuses: assignedSyllabuses,
    }));
  }

  updateUserRole(userId: UserHttpModel['id'], role: UserRole) {
    return this.userRoleBindingsHttpService.updateUserRole(userId, role).pipe(
      tap(() => {
        this.adminUserStore.update(userId, (user) => ({
          ...user,
          role,
        }));
      }),
      this.errorHandlerService.catchError(
        '[AdminUserService]: Failed to update user role',
        () => {
          return EMPTY;
        },
        {
          toast: (err) => ({
            title: 'Failed to update user role',
            description: (err as Error)?.message,
          }),
        },
      ),
    );
  }

  private mapToStoreModels(users: AdminUserHttpModel[]): AdminUserStoreModel[] {
    return users.map((user) => ({
      ...user,
      reviewPermissions: [],
      assigned_syllabuses: user.assigned_syllabuses ?? [],
    }));
  }
}
